import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FeatureToggle from '~/constants/featureToggle'
import { ShipmentItemSearchCriteria } from '~/constants/shipments'
import type { RootState } from '~/store'
import {
  clearInventoryItemTracking,
  fetchInventoryItemTrackingForVariation,
  fetchMoreInventoryItemTrackingForVaccine,
  fetchMoreInventoryItemTrackingForVaccineWithVariation,
  fetchMoreInventoryItemTrackingForVariation,
  getInventoryItemTrackingIsLoading,
  getInventoryItemTrackingList,
  getInventoryItemTrackingListSortedByExpirationDate,
  getInventoryItemTrackingTotalCount,
  getMultipleInventoryItemTrackingItems,
} from '~/store/duck/inventoryItemTracking'
import {
  clearShipmentItems,
  fetchMoreShipmentItemsForVaccine,
  fetchMoreShipmentItemsForVaccineWithVariation,
  fetchMoreShipmentItemsForVariation,
  fetchShipmentItemsForVariation,
  getIsLoading,
  getMultipleShipmentItems,
  getShipmentItemsList,
  getShipmentItemsListSortedByExpirationDate,
  getTotalCount,
} from '~/store/duck/shipmentItems'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { ShipmentItemUnion } from '~/utils/inventoryItemTrackingHelpers'

const SearchCriteriaFetchActionLegacy = {
  [ShipmentItemSearchCriteria.INVENTORY]: fetchMoreShipmentItemsForVariation,
  [ShipmentItemSearchCriteria.PROCEDURE]: fetchMoreShipmentItemsForVaccine,
  [ShipmentItemSearchCriteria.PROCEDURE_WITH_VARIATION]:
    fetchMoreShipmentItemsForVaccineWithVariation,
}

const SearchCriteriaFetchActionEnhanced = {
  [ShipmentItemSearchCriteria.INVENTORY]:
    fetchMoreInventoryItemTrackingForVariation,
  [ShipmentItemSearchCriteria.PROCEDURE]:
    fetchMoreInventoryItemTrackingForVaccine,
  [ShipmentItemSearchCriteria.PROCEDURE_WITH_VARIATION]:
    fetchMoreInventoryItemTrackingForVaccineWithVariation,
}

/**
 * Hook that provides shipment items functionality with automatic feature toggle handling.
 * Returns the appropriate selectors, actions, and state based on the
 * ENHANCED_INVENTORY_ORDER_RECEIVING feature toggle.
 */
export const useInventoryItemTrackingItems = () => {
  const dispatch = useDispatch()
  const isEnhancedEnabled = useSelector(
    getFeatureToggle(FeatureToggle.ENHANCED_INVENTORY_ORDER_RECEIVING),
  )

  const isLoading = useSelector(
    isEnhancedEnabled ? getInventoryItemTrackingIsLoading : getIsLoading,
  )
  const totalCount = useSelector(
    isEnhancedEnabled ? getInventoryItemTrackingTotalCount : getTotalCount,
  )
  const inventoryItemTrackingIds = useSelector(
    isEnhancedEnabled ? getInventoryItemTrackingList : getShipmentItemsList,
  )
  const inventoryItemTrackingList = useSelector(
    isEnhancedEnabled
      ? getInventoryItemTrackingListSortedByExpirationDate
      : getShipmentItemsListSortedByExpirationDate,
  )

  // Memoize selectors to prevent re-creation on every render
  const inventoryItemTrackingItemsSelector = useMemo(
    () =>
      (state: RootState): ShipmentItemUnion[] =>
        isEnhancedEnabled
          ? getMultipleInventoryItemTrackingItems(inventoryItemTrackingIds)(
              state,
            )
          : getMultipleShipmentItems(inventoryItemTrackingIds)(state),
    [isEnhancedEnabled, inventoryItemTrackingIds],
  )
  const inventoryItemTrackingItemsFromSortedSelector = useMemo(
    () =>
      (state: RootState): ShipmentItemUnion[] =>
        isEnhancedEnabled
          ? getMultipleInventoryItemTrackingItems(inventoryItemTrackingList)(
              state,
            )
          : getMultipleShipmentItems(inventoryItemTrackingList)(state),
    [isEnhancedEnabled, inventoryItemTrackingList],
  )

  const inventoryItemTrackingItems = useSelector(
    inventoryItemTrackingItemsSelector,
  )
  const inventoryItemTrackingItemsFromSorted = useSelector(
    inventoryItemTrackingItemsFromSortedSelector,
  )

  const fetchForVariation = useCallback(
    (variationId: string, from: number, to: number) => {
      dispatch(
        isEnhancedEnabled
          ? fetchInventoryItemTrackingForVariation(variationId, from, to)
          : fetchShipmentItemsForVariation(variationId, from, to),
      )
    },
    [dispatch, isEnhancedEnabled],
  )

  const loadMoreItems = useCallback(
    (
      criteria: ShipmentItemSearchCriteria,
      searchCriteriaId: string,
      from: number,
      to: number,
      includeExpiredItems: boolean,
    ) => {
      const fetchAction = isEnhancedEnabled
        ? SearchCriteriaFetchActionEnhanced[criteria]
        : SearchCriteriaFetchActionLegacy[criteria]
      dispatch(fetchAction(searchCriteriaId, from, to, includeExpiredItems))
    },
    [dispatch, isEnhancedEnabled],
  )

  const clear = useCallback(() => {
    dispatch(
      isEnhancedEnabled ? clearInventoryItemTracking() : clearShipmentItems(),
    )
  }, [dispatch, isEnhancedEnabled])

  return useMemo(
    () => ({
      inventoryItemTrackingIds,
      inventoryItemTrackingList,
      inventoryItemTrackingItems: inventoryItemTrackingItems || [],
      inventoryItemTrackingItemsFromSorted:
        inventoryItemTrackingItemsFromSorted || [],
      isLoading,
      totalCount,
      fetchForVariation,
      loadMoreItems,
      clear,
    }),
    [
      inventoryItemTrackingIds,
      inventoryItemTrackingList,
      inventoryItemTrackingItems,
      inventoryItemTrackingItemsFromSorted,
      isLoading,
      totalCount,
      fetchForVariation,
      loadMoreItems,
      clear,
    ],
  )
}
